<script lang="ts">
    import { putDBEntry } from "../../../api"
    import Modal from "../../components/Modal.svelte"
    import Cta from "../../components/CTA.svelte"
    import hash from "object-hash"
    import { actionApproval, isMobile, newNotification } from "../../store"
    import MainModalLowerLine from "./MainModalLowerLine.svelte"
    import { copyObject } from "../utils"
    import { getCachedEntries } from "../../../api"
    import ManualProtocolBody from "./ManualProtocolBody.svelte"
    import { spaNavigate } from "../../actions"

    import { createEventDispatcher, onDestroy } from "svelte"
    export let task: TaskOilSpillRemoval | TaskTowing,
        showModal = false,
        standalone = false,
        onBack: () => void = () => {
            if (task?.orderId && task?.id) {
                spaNavigate(`/order/${task.orderId}/task/${task.id}?manualProtocol=1`, { replace: true })
            } else {
                spaNavigate("/", { replace: true })
            }
        }
    let originalTaskProtocolHash = "",
        originalObject = copyObject(task.manualProtocol)
    const dispatch = createEventDispatcher()

    type ManualProtocolData = NonNullable<TaskOilSpillRemoval["manualProtocol"]>

    type SaveOptions = {
        notifySuccess?: boolean
        exitEditMode?: boolean
    }

    const AUTO_SAVE_DELAY = 400
    const TOP_TIMESTAMP_INTERVAL = 1000
    const DEFAULT_TOWING_PROTOCOL_EVENT = "Beauftragung"

    let towingAssignmentOptionId: string | null = null
    let towingAssignmentLoading = false

    let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null
    let saveQueue: Promise<void> = Promise.resolve()
    let topTimestampInterval: ReturnType<typeof setInterval> | null = null
    let topTimestampManuallySet = false

    function normalizeManualProtocol(protocol: ManualProtocolData | undefined) {
        const normalized = copyObject(
            protocol || {
                entries: [],
                additionalInformation: "",
            }
        )

        if (!Array.isArray(normalized.entries)) normalized.entries = []

        normalized.entries = normalized.entries.map((entry) => {
            if (!entry?.eventId) {
                return {
                    ...entry,
                    datetime: null,
                }
            }
            return entry
        })

        return normalized
    }

    function cancelAutoSave() {
        if (!autoSaveTimeout) return
        clearTimeout(autoSaveTimeout)
        autoSaveTimeout = null
    }

    function scheduleAutoSave() {
        cancelAutoSave()
        autoSaveTimeout = setTimeout(() => {
            autoSaveTimeout = null
            enqueueSave()
        }, AUTO_SAVE_DELAY)
    }

    async function performSave(options: SaveOptions = {}) {
        if (isSaveDisabled || !task?.id) return

        const { notifySuccess = false, exitEditMode = false } = options
        let success = false

        try {
            // @ts-ignore
            await putDBEntry("task", {
                id: task.id,
                manualProtocol: task.manualProtocol,
            })
            newNotification({
                html: "Aktuallisierung gespeichert...",
                class: "success",
                timeout: 1000,
            })
            success = true
        } catch (error) {
            console.error("Error updating task", error)
            newNotification({
                html: "Fehler beim Aktualisieren der Zeitstempel",
                class: "error",
            })
        }

        if (!success) return

        dispatch("updateTaskProtocol", task.manualProtocol)
        setTimeout(() => {
            originalTaskProtocolHash = hash(normalizeManualProtocol(task.manualProtocol || {}))
            originalObject = copyObject(task.manualProtocol)
            if (exitEditMode) editMode = false
            if (notifySuccess) {
                newNotification({
                    html: "Zeitstempel aktuallisiert",
                    class: "success",
                })
            }
        }, 100)
    }

    function enqueueSave(options: SaveOptions = {}) {
        saveQueue = saveQueue
            .catch(() => {})
            .then(() => performSave(options))
            .catch((error) => {
                console.error("Error saving manual protocol", error)
            })
        return saveQueue
    }

    function stopTopTimestampTicker() {
        if (!topTimestampInterval) return
        clearInterval(topTimestampInterval)
        topTimestampInterval = null
    }

    function updateTopTimestamp() {
        const entries = task?.manualProtocol?.entries
        if (!entries?.length) {
            stopTopTimestampTicker()
            return
        }

        const [firstEntry, ...rest] = entries
        if (!firstEntry || firstEntry.eventId || topTimestampManuallySet) {
            stopTopTimestampTicker()
            return
        }

        task.manualProtocol.entries = [
            {
                ...firstEntry,
                datetime: new Date(),
            },
            ...rest,
        ]
    }

    function startTopTimestampTicker() {
        if (topTimestampInterval) return
        updateTopTimestamp()
        topTimestampInterval = setInterval(() => updateTopTimestamp(), TOP_TIMESTAMP_INTERVAL)
    }
    $: if (!task?.manualProtocol) {
        task.manualProtocol = {
            entries: [],
            additionalInformation: "",
        }
        originalTaskProtocolHash = hash(normalizeManualProtocol(task.manualProtocol))
        originalObject = copyObject(task.manualProtocol)
    }
    $: if (!task?.manualProtocol?.entries?.length) {
        task.manualProtocol.entries = [
            {
                datetime: new Date(),
                eventId: "",
            },
        ]
        task.manualProtocol.additionalInformation = task.manualProtocol.additionalInformation ?? ""
        originalTaskProtocolHash = hash(normalizeManualProtocol(task.manualProtocol))
        originalObject = copyObject(task.manualProtocol)
        topTimestampManuallySet = false
    }
    $: if (task?.manualProtocol && !originalTaskProtocolHash) {
        originalTaskProtocolHash = hash(normalizeManualProtocol(task.manualProtocol))
        originalObject = copyObject(task.manualProtocol)
    }

    let editMode = false
    let refresh = false
    let isSaveDisabled = true

    function toDate(value: Date | string | null | undefined): Date | null {
        if (!value) return null
        const parsed = value instanceof Date ? value : new Date(value)
        return isNaN(parsed.getTime()) ? null : parsed
    }

    async function loadTowingAssignmentOptionId() {
        if (towingAssignmentOptionId || towingAssignmentLoading) return
        towingAssignmentLoading = true
        try {
            const [option] = await getCachedEntries("option", {
                category: "taskEvent",
                name: DEFAULT_TOWING_PROTOCOL_EVENT,
                "additionalInfos.taskTypes": "towing",
            })
            towingAssignmentOptionId = (option?.id || option?._id) ?? null
        } catch (error) {
            console.error("Beauftragung-Option konnte nicht geladen werden", error)
            towingAssignmentOptionId = null
        } finally {
            towingAssignmentLoading = false
        }
    }

    async function ensureTowingAssignmentEntry() {
        if (!task || task.taskType !== "towing") return
        ensureProtocolStructure()
        await loadTowingAssignmentOptionId()
        if (!towingAssignmentOptionId) return

        const insertTimestamp = toDate(task.insertTime) ?? new Date()
        const entries = task.manualProtocol.entries || []
        const existingIndex = entries.findIndex((entry) => entry?.eventId === towingAssignmentOptionId)
        const hasAnyEvent = entries.some((entry) => entry?.eventId)

        if (existingIndex >= 0) {
            const currentEntry = entries[existingIndex]
            const currentDate = toDate(currentEntry?.datetime)
            if (!currentDate || currentDate.getTime() !== insertTimestamp.getTime()) {
                task.manualProtocol.entries = entries.map((entry, idx) =>
                    idx === existingIndex
                        ? {
                              ...entry,
                              datetime: insertTimestamp,
                          }
                        : entry
                )
                refresh = !refresh
                scheduleAutoSave()
            }
            return
        }

        if (!hasAnyEvent && entries.length) {
            const [firstEntry, ...restEntries] = entries
            task.manualProtocol.entries = [
                {
                    ...firstEntry,
                    datetime: insertTimestamp,
                    eventId: towingAssignmentOptionId,
                },
                ...restEntries.filter((entry) => entry?.eventId !== towingAssignmentOptionId),
            ]
        } else {
            task.manualProtocol.entries = [
                {
                    datetime: insertTimestamp,
                    eventId: towingAssignmentOptionId,
                },
                ...entries.filter((entry) => entry?.eventId !== towingAssignmentOptionId),
            ]
        }

        refresh = !refresh
        topTimestampManuallySet = true
        scheduleAutoSave()
    }

    // wenn das erste element einen wert hat in ereignis, füge eine neue zeile am anfang hinzu
    $: {
        const firstEntry = task?.manualProtocol?.entries?.[0]
        const skipAutoPrepend =
            task?.taskType === "towing" && towingAssignmentOptionId && firstEntry?.eventId === towingAssignmentOptionId

        if (firstEntry?.eventId && !skipAutoPrepend) {
            task.manualProtocol.entries = [
                {
                    datetime: new Date(),
                    eventId: "",
                },
                ...task.manualProtocol.entries,
            ]
            refresh = !refresh
            topTimestampManuallySet = false
        }
    }
    $: isSaveDisabled = originalTaskProtocolHash === hash(normalizeManualProtocol(task.manualProtocol || {}))

    $: if (task?.taskType === "towing") {
        ensureTowingAssignmentEntry()
    }

    $: {
        const firstEntry = task?.manualProtocol?.entries?.[0]
        if (firstEntry && !firstEntry.eventId) {
            if (!topTimestampManuallySet) startTopTimestampTicker()
            else stopTopTimestampTicker()
        } else {
            stopTopTimestampTicker()
            topTimestampManuallySet = false
        }
    }

    function resetManualProtocol() {
        cancelAutoSave()
        stopTopTimestampTicker()
        topTimestampManuallySet = false
        task.manualProtocol = copyObject(originalObject)
        originalTaskProtocolHash = hash(normalizeManualProtocol(task.manualProtocol || {}))
        editMode = false
        refresh = !refresh
    }

    function handleModalClose() {
        resetManualProtocol()
        showModal = false
    }

    function handleToggleEditMode(event: MouseEvent) {
        event?.stopPropagation()
        event?.preventDefault()
        editMode = true
    }

    function ensureProtocolStructure() {
        if (!task.manualProtocol) {
            task.manualProtocol = {
                entries: [],
                additionalInformation: "",
            }
        }
        if (!Array.isArray(task.manualProtocol.entries)) {
            task.manualProtocol.entries = []
        }
        task.manualProtocol.additionalInformation = task.manualProtocol.additionalInformation ?? ""
    }

    $: if (task?.manualProtocol?.entries) {
        const needsNormalization = task.manualProtocol.entries.some(
            (entry) => entry?.datetime && !(entry.datetime instanceof Date)
        )
        if (needsNormalization) {
            task.manualProtocol.entries = task.manualProtocol.entries.map((entry) => {
                if (entry?.datetime && !(entry.datetime instanceof Date)) {
                    return {
                        ...entry,
                        datetime: new Date(entry.datetime),
                    }
                }
                return entry
            })
            refresh = !refresh
        }
    }

    function handleAddEntry(event: MouseEvent) {
        event?.stopPropagation()
        event?.preventDefault()
        editMode = true
        ensureProtocolStructure()
        task.manualProtocol.entries = [
            {
                datetime: new Date(),
                eventId: "",
            },
            ...(task.manualProtocol?.entries || []),
        ]
        refresh = !refresh
        topTimestampManuallySet = false
    }

    function handleDeleteEntry(index: number) {
        $actionApproval = {
            modalTitle: "Löschen",
            modalText: "Möchten Sie diesen Zeitstempel wirklich löschen?",
            callback: () => {
                task.manualProtocol.entries = task.manualProtocol.entries.filter((_, i) => i !== index)
                editMode = false
                refresh = !refresh
                scheduleAutoSave()
            },
        }
    }

    function handleEntryChanged(_index: number) {
        task.manualProtocol.entries = [...(task.manualProtocol.entries || [])]
        if (_index === 0 && !task.manualProtocol.entries?.[0]?.eventId) {
            topTimestampManuallySet = true
        }
        scheduleAutoSave()
    }

    function handleAdditionalInfoBlur(valueChanged: boolean) {
        if (!valueChanged) return
        scheduleAutoSave()
    }

    function handleSave(event: MouseEvent) {
        event?.stopPropagation()
        event?.preventDefault()
        if (isSaveDisabled) return

        cancelAutoSave()
        enqueueSave({ notifySuccess: true, exitEditMode: true })
    }

    function handleOpenStandalone(event: MouseEvent) {
        event?.stopPropagation()
        event?.preventDefault()
        showModal = false
        spaNavigate(`/order/${task?.orderId}/task/${task?.id}/manual-protocol`)
    }

    function handleBack(event: MouseEvent) {
        event?.stopPropagation()
        event?.preventDefault()
        if (!isSaveDisabled) {
            $actionApproval = {
                modalTitle: "Verlassen",
                modalText: "Sie haben ungespeicherte Daten, möchten Sie den Eintrag dennoch ungespeichert verlassen?",
                callback: () => {
                    resetManualProtocol()
                    onBack?.()
                },
            }
            return
        }
        resetManualProtocol()
        onBack?.()
    }

    onDestroy(() => {
        cancelAutoSave()
        stopTopTimestampTicker()
    })
</script>

{#if standalone}
    <section class="manual-protocol standalone">
        <div class="inner-modal-header">
            <div class="upper">
                <Cta
                    type="BubbleButton"
                    icon="solar_arrow-left-outline"
                    content="{$isMobile ? 'Zurück' : 'Zurück zum Einsatz'}"
                    on:click="{handleBack}"
                />
                <div class="title">
                    <img
                        src="../../../assets/icons/solar_alarm-add-outline.svg"
                        alt="icon"
                    />
                    <p>Zeitstempel</p>
                </div>
            </div>
            <MainModalLowerLine task="{task}" />
        </div>
        <ManualProtocolBody
            task="{task}"
            editMode="{editMode}"
            refresh="{refresh}"
            onDeleteEntry="{handleDeleteEntry}"
            onEntryChanged="{handleEntryChanged}"
            onAdditionalInfoBlur="{handleAdditionalInfoBlur}"
        />
        <div class="inner-footer">
            <div>
                {#if editMode}
                    <Cta
                        type="tertiaryButton"
                        icon="mage_plus"
                        content="{$isMobile ? 'Zeitstempel' : 'Zeitstempel hinzufügen'}"
                        on:click="{handleAddEntry}"
                    />
                {:else}
                    <Cta
                        type="tertiaryButton"
                        icon="solar_pen-outline"
                        content="{$isMobile ? 'Zeitstempel' : 'Zeitstempel bearbeiten'}"
                        on:click="{handleToggleEditMode}"
                    />
                {/if}
            </div>
            <div>
                <Cta
                    type="primaryButton"
                    icon="solar_diskette-outline"
                    content="Speichern"
                    disabled="{isSaveDisabled}"
                    on:click="{handleSave}"
                />
            </div>
        </div>
    </section>
{:else if showModal}
    <Modal
        show="{showModal}"
        mainModal="{true}"
        showCloseIcon="{true}"
        size="xl"
        on:close="{handleModalClose}"
    >
        <div
            slot="header"
            class="inner-modal-header"
        >
            <div class="upper">
                <div class="title">
                    <img
                        src="../../../assets/icons/solar_alarm-add-outline.svg"
                        alt="icon"
                    />
                    <p>Zeitstempel</p>
                </div>
            </div>
            <MainModalLowerLine task="{task}" />
        </div>
        <ManualProtocolBody
            task="{task}"
            editMode="{editMode}"
            refresh="{refresh}"
            onDeleteEntry="{handleDeleteEntry}"
            onEntryChanged="{handleEntryChanged}"
            onAdditionalInfoBlur="{handleAdditionalInfoBlur}"
        />
        <div
            class="inner-footer"
            slot="footer"
        >
            <div>
                {#if editMode}
                    <Cta
                        type="tertiaryButton"
                        icon="mage_plus"
                        content="{$isMobile ? 'Zeitstempel' : 'Zeitstempel hinzufügen'}"
                        on:click="{handleAddEntry}"
                    />
                {:else}
                    <Cta
                        type="tertiaryButton"
                        icon="solar_pen-outline"
                        content="{$isMobile ? 'Zeitstempel' : 'Zeitstempel bearbeiten'}"
                        on:click="{handleToggleEditMode}"
                    />
                {/if}
                <Cta
                    type="tertiaryButton"
                    icon="solar_login-outline"
                    content="{$isMobile ? 'Seite' : 'In Vollansicht öffnen'}"
                    on:click="{handleOpenStandalone}"
                />
            </div>
            <div>
                <Cta
                    type="primaryButton"
                    icon="solar_diskette-outline"
                    content="Speichern"
                    disabled="{isSaveDisabled}"
                    on:click="{handleSave}"
                />
            </div>
        </div>
    </Modal>
{/if}

<style
    lang="less"
    global
>
    @import "../../../../assets/variables.less";
    .inner-modal-header {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        max-width: 100%;
        gap: 1.2rem;
        color: var(--white-100);
        background-color: var(--text-000);
        border-radius: 1.2rem 1.2rem 0px 0px;
        .upper {
            display: flex;
            width: 100%;
            justify-content: space-between;
            align-items: center;
            .title {
                display: flex;
                gap: 0.6rem;
                align-items: center;
                p {
                    font-size: 1.2rem;
                }
            }
        }
    }
    .manual-protocol.standalone {
        display: flex;
        flex-direction: column;
        gap: 1.6rem;
        width: 100%;
        max-width: 960px;
        margin: 0 auto;
        padding: 2.4rem 0;
    }
    .manual-protocol.standalone .inner-modal-header {
        background-color: transparent;
        color: var(--text-100);
        border-radius: 0;
        padding: 0;
    }
    .manual-protocol.standalone .inner-modal-header .upper {
        justify-content: flex-start;
        gap: 1rem;
    }
    .manual-protocol.standalone .inner-modal-header .lower * {
        color: var(--text-100) !important;
    }
    .manual-protocol.standalone .inner-modal-header .title p {
        color: var(--text-100);
    }
    .manual-protocol.standalone .main-wrapper {
        background: var(--white-100);
        border: 1px solid var(--grey-300);
        border-radius: 12px;
        padding: 1.2rem;
    }
    .manual-protocol.standalone .main-wrapper .wrapper {
        padding: 0;
    }
    .inner-footer {
        display: flex;
        width: 100%;
        justify-content: space-between;
        background-color: var(--grey-300);
        padding: 0.6rem 0rem;
        border-radius: 0px 0px 1.2rem 1.2rem;
        div {
            display: flex;
            gap: 0.6rem;
            flex-wrap: wrap;
        }
    }
    .manual-protocol.standalone .inner-footer {
        background-color: transparent;
        border-radius: 0;
        padding: 1.2rem 0 0;
        border-top: 1px solid var(--grey-300);
    }
</style>
